import { PolicyType } from 'pbts/c1/api/policy/v1/policy'
import { StepApprovalType } from 'pbts/c1/api/policy/v1/policy_steps_approval'
import { IntlShape } from 'react-intl'

export enum PolicyTypeStrings {
    Grant = 'grant',
    Revoke = 'revoke',
    Certify = 'certify',
    Unspecified = 'unspecified',
}

export enum ApprovalType {
    Users = 'Users',
    Manager = 'Manager',
    AppOwners = 'AppOwners',
    Group = 'Group',
    Self = 'Self',
    EntitlementOwners = 'EntitlementOwners',
    NotSet = 'NotSet',
    Expression = 'Expression',
    Webhook = 'Webhook',
    ResourceOwners = 'ResourceOwners',
    Agent = 'Agent',
}

export enum StepApprovalTypeDisplayString {
    Users = 'Users',
    Manager = 'Manager',
    AppOwners = 'App Owners',
    Group = 'Group',
    Self = 'Self',
    EntitlementOwners = 'Entitlement owners',
    Expression = 'Expression',
    Webhook = 'Webhook',
    ResourceOwners = 'Resource owners',
    Agent = 'Agent',
}

export enum ApprovalOneOfKind {
    USERS = 'users',
    MANAGER = 'manager',
    APP_OWNERS = 'appOwners',
    ENTITLEMENT_OWNERS = 'entitlementOwners',
    GROUP = 'group',
    SELF = 'self',
    EXPRESSION = 'expression',
    WEBHOOK = 'webhook',
    RESOURCE_OWNERS = 'resourceOwners',
    AGENT = 'agent',
}

export enum EscalationOneOfKind {
    REPLACE_POLICY = 'replacePolicy',
    REASSIGN_TO_APPROVERS = 'reassignToApprovers',
    CANCEL_TICKET = 'cancelTicket',
    SKIP_STEP = 'skipStep',
}

export enum StepOneOfKind {
    ACCEPT = 'accept',
    REJECT = 'reject',
    APPROVAL = 'approval',
    WAIT = 'wait',
}

export enum ProvisionOneOfKind {
    CONNECTOR = 'connector',
    MANUAL = 'manual',
    DELEGATED = 'delegated',
    WEBHOOK = 'webhook',
    MULTI_STEP = 'multiStep',
    EXTERNAL_TICKET = 'externalTicket',
    ACTION = 'action',
    UNCONFIGURED = 'unconfigured'
}

export enum ProvisionerAssignmentOneOfKind {
    USERS = 'users',
    APP_OWNERS = 'appOwners',
    GROUP = 'group',
    MANAGER = 'manager',
    EXPRESSION = 'expression',
    ENTITLEMENT_OWNERS = 'entitlementOwners',
}

export enum ConnectorProvisionOneOfKind {
    ACCOUNT = 'account',
    DEFAULT_BEHAVIOR = 'defaultBehavior',
    DELETE_ACCOUNT = 'deleteAccount',
}

export enum PasswordStorageOneOfKind {
    SAVE_TO_VAULT = 'saveToVault',
    DO_NOT_SAVE = 'doNotSave',
}

export const policyTypeToString = (option?: PolicyType): string => {
    switch (option) {
        case PolicyType.GRANT:
            return PolicyTypeStrings.Grant
        case PolicyType.REVOKE:
            return PolicyTypeStrings.Revoke
        case PolicyType.CERTIFY:
            return PolicyTypeStrings.Certify
        case PolicyType.UNSPECIFIED:
        default:
            return PolicyTypeStrings.Unspecified
    }
}

export const policyTypeFromString = (option?: string): PolicyType => {
    switch (option) {
        case PolicyTypeStrings.Grant:
            return PolicyType.GRANT
        case PolicyTypeStrings.Revoke:
            return PolicyType.REVOKE
        case PolicyTypeStrings.Certify:
            return PolicyType.CERTIFY
        case PolicyTypeStrings.Unspecified:
        default:
            return PolicyType.UNSPECIFIED
    }
}

export const policyTypeToIntl = (pt: PolicyType | undefined, intl: IntlShape): string => {
    const unknown = intl.formatMessage({ id: 'Components_Policy_Type_Unknown', defaultMessage: 'Unknown' })
    if (!pt) {
        return unknown
    }
    switch (pt) {
        case PolicyType.REVOKE:
            return intl.formatMessage({ id: 'Components_Policy_Type_Revoke', defaultMessage: 'Revoke' })
        case PolicyType.GRANT:
            return intl.formatMessage({ id: 'Components_Policy_Type_Request', defaultMessage: 'Request' })
        case PolicyType.CERTIFY:
            return intl.formatMessage({ id: 'Components_Policy_Type_Review', defaultMessage: 'Review' })
        default:
            return unknown
    }
}

export const StepApprovalTypeEnumToString = (option?: StepApprovalType): string => {
    switch (option) {
        case StepApprovalType.USERS:
            return StepApprovalTypeDisplayString.Users
        case StepApprovalType.MANAGER:
            return StepApprovalTypeDisplayString.Manager
        case StepApprovalType.APP_OWNERS:
            return StepApprovalTypeDisplayString.AppOwners
        case StepApprovalType.ENTITLEMENT_OWNERS:
            return StepApprovalTypeDisplayString.EntitlementOwners
        case StepApprovalType.GROUP:
            return StepApprovalTypeDisplayString.Group
        case StepApprovalType.SELF:
            return StepApprovalTypeDisplayString.Self
        case StepApprovalType.EXPRESSION:
            return StepApprovalTypeDisplayString.Expression
        case StepApprovalType.WEBHOOK:
            return StepApprovalTypeDisplayString.Webhook
        case StepApprovalType.RESOURCE_OWNERS:
            return StepApprovalTypeDisplayString.ResourceOwners
        case StepApprovalType.AGENT:
            return StepApprovalTypeDisplayString.Agent
        default:
            return 'Unknown'
    }
}

export const i18nApprovalStepType = (ApprovalTypeTSString: string, intl: IntlShape): string => {
    switch (ApprovalTypeTSString) {
        case ApprovalType.Users:
            return intl.formatMessage({
                id: 'component_policy_type_string_users',
                defaultMessage: 'User',
            })
        case ApprovalType.Manager:
            return intl.formatMessage({
                id: 'component_policy_type_string_managers',
                defaultMessage: 'Manager',
            })
        case ApprovalType.AppOwners:
            return intl.formatMessage({
                id: 'component_policy_type_string_app_owners',
                defaultMessage: 'App owner',
            })
        case ApprovalType.EntitlementOwners:
            return intl.formatMessage({
                id: 'component_policy_type_string_self',
                defaultMessage: 'Entitlement owner',
            })
        case ApprovalType.Group:
            return intl.formatMessage({
                id: 'component_policy_type_string_app_owners',
                defaultMessage: 'Group',
            })
        case ApprovalType.Self:
            return intl.formatMessage({
                id: 'component_policy_type_string_self',
                defaultMessage: 'Account owner',
            })
        case ApprovalType.Expression:
            return intl.formatMessage({
                id: 'component_policy_type_string_expression',
                defaultMessage: 'Expression',
            })
        case ApprovalType.ResourceOwners:
            return intl.formatMessage({
                id: 'component_policy_type_string_resource_owners',
                defaultMessage: 'Resource owner',
            })
        case ApprovalType.Agent:
            return intl.formatMessage({
                id: 'component_policy_type_string_agent',
                defaultMessage: 'Agent',
            })
        case ApprovalType.NotSet:
        default:
            return intl.formatMessage({
                id: 'component_policy_type_string_unknown',
                defaultMessage: 'UNKNOWN',
            })
    }
}
